import React from "react";
import { 
    CircleCheck,
    Info,
    TrendingUp,
    Users,
    Star,
    CircleAlert,
} from "lucide-react";
import Card from "components/card";
import QuickFactCard from "./QuickFactCard";
import { QUICK_FACTS, LOCATION_THEMES } from "variables/constants";
import RoleSourcing from "./RoleSourcing";

const LocationDetails = ({ country }) => {
    if (!country) return <p className="p-4">Select a location to see details.</p>;
    
    const theme = LOCATION_THEMES[country.country];

    return (
        <Card className="bg-white text-slate-900 flex flex-col gap-6 rounded-2xl border-2 border-slate-200 shadow-xl overflow-hidden dark-mode-card">
            <div className={`pb-6 location-theme ${theme} bg border-b p-4 sm:p-6`}>
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                        <span className="text-5xl sm:text-6xl">
                            {country.flag}
                        </span>
                        <div>
                            <h4 className="text-[20px] sm:text-[24px] mb-1 title-text">
                                {country.country}
                            </h4>
                            <p className="text-[13px] sm:text-[14px] muted-text">
                                {country.description}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="pb-6 p-4 sm:p-6">
                <div className="space-y-6">
                    <div>
                        <h3 className="text-[16px] sm:text-[18px] font-semibold mb-4 flex items-center gap-2 title-text">
                            <Info className="w-5 h-5 text-indigo-600 dark:text-indigo-500" />
                            Quick Facts
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {QUICK_FACTS.map(fact => {
                                const tags = fact.id === "keyIndustries" ? country.keyIndustries : null;
                                const value = tags ? null : country[fact.id.replace("-", "")] || country[fact.id];

                                return (
                                    <QuickFactCard
                                        key={fact.id}
                                        icon={fact.icon}
                                        label={fact.label}
                                        value={value}
                                        tags={tags}
                                        iconColor={fact.iconColor}
                                        bgColor={fact.bgColor}
                                        textColor={fact.textColor}
                                        border={fact.border}
                                    />
                                );
                            })}
                        </div>
                    </div>
                    <div className="bg-slate-200 shrink-0 h-px w-full"></div>
                    <div>
                        <h3 className="text-[16px] sm:text-[18px] font-semibold mb-4 flex items-center gap-2 title-text">
                            <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-500" />
                            Key Advantages
                        </h3>
                        <div className="text-slate-900 flex flex-col gap-6 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900 dark:to-white/5">
                            <div className="pb-6 p-4 sm:p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {country.keyAdvantages?.map((advantage, index) => (
                                        <div
                                            key={index}
                                            className="flex items-start gap-3 p-3 sm:p-4 rounded-xl border-2 border-emerald-200 hover:border-emerald-300 hover:shadow-md transition-all opacity-100 dark-mode-card"
                                        >
                                            <CircleCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5 dark:text-emerald-500" />
                                            <p className="text-[12px] sm:text-[13px] leading-relaxed muted-text">
                                                {advantage}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-slate-200 shrink-0 h-px w-full"></div>
                    <div>
                        <h3 className="text-[16px] sm:text-[18px] font-semibold mb-4 flex items-center gap-2 title-text">
                            <Users className="w-5 h-5 text-purple-600 dark:text-purple-500" />
                            Workforce Strengths & Attributes
                        </h3>
                        <div className="text-slate-900 flex flex-col gap-6 rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900 dark:to-pink-900">
                            <div className="pb-6 p-4 sm:p-6">
                                <div className="space-y-3">
                                    {country.attributes?.map((attribute, index) => (
                                        <div 
                                            key={index}
                                            className="flex items-start gap-3 p-3 sm:p-4 rounded-2xl border-2 border-purple-200 hover:border-purple-300 hover:shadow-md transition-all opacity-100 dark-mode-card"
                                        >
                                            <div className={`p-2 rounded-xl flex-shrink-0  location-theme ${theme} bg-color`}>
                                                <Star className="w-4 h-4 text-white" />
                                            </div>
                                            <p className="text-[12px] sm:text-[13px] leading-relaxed flex-1 muted-text">
                                                {attribute}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-slate-200 shrink-0 h-px w-full"></div>
                    <RoleSourcing roles={country.recommendedRoles} />
                    <div className="bg-slate-200 shrink-0 h-px w-full"></div>
                    <div>
                        <h3 className="text-[16px] sm:text-[18px] font-semibold mb-4 flex items-center gap-2 title-text">
                            <CircleAlert className="w-5 h-5 text-orange-600 dark:text-orange-500" />
                            Important Considerations
                        </h3>
                        <div className="text-slate-900 flex flex-col gap-6 rounded-2xl border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white dark:from-orange-900 dark:to-white/10">
                            <div className="pb-6 p-4 sm:p-6">
                                <div className="grid grid-cols-1 gap-3">
                                    {country.importantConsiderations?.map((important, index) => (
                                        <div
                                            key={index}
                                            className="flex items-start gap-3 p-3 sm:p-4 bg-white rounded-xl border-2 border-orange-200 hover:border-orange-300 hover:shadow-md transition-all opacity-100 dark-mode-card"
                                        >
                                            <CircleCheck className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5 dark:text-orange-500 " />
                                            <p className="text-[12px] sm:text-[13px] leading-relaxed muted-text">
                                                {important}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default LocationDetails;
